// Format time to friendly format
export const formatFriendlyTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) {
    return 'Just nu';
  } else if (diffMins < 60) {
    return `${diffMins} ${diffMins === 1 ? 'minut' : 'minuter'} sedan`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'timme' : 'timmar'} sedan`;
  } else if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? 'dag' : 'dagar'} sedan`;
  } else {
    return date.toLocaleDateString('sv-SE', { 
      day: 'numeric', 
      month: 'short', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  }
};

// Capitalize first letter
export const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

// Get category CSS class - matching frontend-web logic
export const getCategoryClass = (category) => {
  if (!category) return 'default';
  
  const validCategories = [
    'arbetsliv', 'ekonomi', 'film', 'forskning', 'hållbarhet', 'hälsa', 
    'hem kök och trädgård', 'kultur', 'miljö', 'mode', 
    'politik', 'sport', 'teknik', 'underhållning', 'utbildning', 'utrikes', 'övrigt'
  ];
  
  // Normalize category for CSS class usage - same as frontend-web
  const normalizedCategory = category.toLowerCase()
    .replace(/[,\s]+/g, '-')  // Replace comma and spaces with dash
    .replace(/[åä]/g, 'a')    // Replace å and ä with a
    .replace(/ö/g, 'o');      // Replace ö with o
  
  // Check if it's a valid category
  if (validCategories.includes(category.toLowerCase())) {
    return normalizedCategory;
  }
  
  // Check legacy categories
  const legacyMap = {
    'nyheter': 'nyheter',
    'vetenskap': 'vetenskap',
    'nöje': 'noje',
    'resor': 'resor',
    'mat & dryck': 'mat-dryck'
  };
  
  return legacyMap[category.toLowerCase()] || 'default';
};

// Local storage helpers for read articles - using same key as frontend-web
export const getReadArticles = () => {
  try {
    const stored = localStorage.getItem('read_articles');  // Changed from 'readArticles' to match web version
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting read articles:', error);
    return [];
  }
};

export const saveReadArticles = (articles) => {
  try {
    localStorage.setItem('read_articles', JSON.stringify(articles));  // Changed from 'readArticles' to match web version
  } catch (error) {
    console.error('Error saving read articles:', error);
  }
};

export const markArticleAsRead = (articleId) => {
  const readArticles = getReadArticles();
  if (!readArticles.includes(articleId)) {
    readArticles.push(articleId);
    saveReadArticles(readArticles);
  }
};

// Generate URL-friendly slug from title
export const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Theme helpers
export const getTheme = () => {
  try {
    return localStorage.getItem('theme') || 'dark';
  } catch (error) {
    return 'dark';
  }
};

export const saveTheme = (theme) => {
  try {
    localStorage.setItem('theme', theme);
    if (theme === 'light') {
      document.body.classList.add('light-mode');
    } else {
      document.body.classList.remove('light-mode');
    }
  } catch (error) {
    console.error('Error saving theme:', error);
  }
};

// Initialize theme on app start
export const initializeTheme = () => {
  const theme = getTheme();
  if (theme === 'light') {
    document.body.classList.add('light-mode');
  }
};

// Migration function to convert old localStorage keys to new structure
export const migrateLocalStorageKeys = () => {
  try {
    // Migrate readArticles to read_articles
    const oldReadArticles = localStorage.getItem('readArticles');
    if (oldReadArticles && !localStorage.getItem('read_articles')) {
      localStorage.setItem('read_articles', oldReadArticles);
      localStorage.removeItem('readArticles');
      console.log('Migrated readArticles to read_articles');
    }

    // Migrate selectedRegions and selectedCategories to filters object
    const oldSelectedRegions = localStorage.getItem('selectedRegions');
    const oldSelectedCategories = localStorage.getItem('selectedCategories');
    
    if ((oldSelectedRegions || oldSelectedCategories) && !localStorage.getItem('filters')) {
      const filters = {
        regions: oldSelectedRegions ? JSON.parse(oldSelectedRegions) : [],
        categories: oldSelectedCategories ? JSON.parse(oldSelectedCategories) : [],
        sources: []  // No old source filters to migrate
      };
      localStorage.setItem('filters', JSON.stringify(filters));
      
      // Mark as customized if they had custom selections
      if (oldSelectedRegions || oldSelectedCategories) {
        localStorage.setItem('user_has_customized_filters', 'true');
      }
      
      // Remove old keys
      localStorage.removeItem('selectedRegions');
      localStorage.removeItem('selectedCategories');
      console.log('Migrated filter settings to new structure');
    }
  } catch (error) {
    console.error('Error migrating localStorage keys:', error);
  }
};

// Filter helpers - using same structure as frontend-web
export const getFilters = () => {
  try {
    const stored = localStorage.getItem('filters');
    if (stored) {
      const filters = JSON.parse(stored);
      // Ensure all keys exist
      return {
        categories: filters.categories || [],
        sources: filters.sources || [],
        regions: filters.regions || []
      };
    }
    return { categories: [], sources: [], regions: [] };
  } catch (error) {
    console.error('Error getting filters:', error);
    return { categories: [], sources: [], regions: [] };
  }
};

export const saveFilters = (filters) => {
  try {
    const normalized = {
      categories: filters.categories || [],
      sources: filters.sources || [],
      regions: filters.regions || []
    };
    localStorage.setItem('filters', JSON.stringify(normalized));
  } catch (error) {
    console.error('Error saving filters:', error);
  }
};

// Helper to track if user has customized filters
export const hasUserMadeManualSelection = () => {
  return localStorage.getItem('user_has_customized_filters') === 'true';
};

export const markUserAsHavingCustomizedFilters = () => {
  localStorage.setItem('user_has_customized_filters', 'true');
};

// Region filter helpers - now using filters object
export const getSelectedRegions = () => {
  const filters = getFilters();
  return filters.regions || [];
};

export const saveSelectedRegions = (regions) => {
  const filters = getFilters();
  filters.regions = regions;
  saveFilters(filters);
  markUserAsHavingCustomizedFilters();
};

// Category filter helpers - now using filters object
export const getSelectedCategories = () => {
  const filters = getFilters();
  return filters.categories || [];
};

export const saveSelectedCategories = (categories) => {
  const filters = getFilters();
  filters.categories = categories;
  saveFilters(filters);
  markUserAsHavingCustomizedFilters();
};

// =============================================================================
// PLAUSIBLE ANALYTICS TRACKING
// =============================================================================

/**
 * Track a user event with Plausible Analytics
 * @param {string} eventName - Name of the event (e.g., 'article-opened')
 * @param {object} props - Optional properties to attach to the event
 */
export const trackEvent = (eventName, props = {}) => {
  try {
    if (typeof window.plausible === 'function') {
      window.plausible(eventName, { props });
      console.log(`📊 Event tracked: ${eventName}`, props);
    } else {
      console.warn('⚠️  Plausible not loaded yet, event not tracked:', eventName);
    }
  } catch (error) {
    console.error('❌ Error tracking event:', eventName, error);
  }
};

/**
 * Track an error with full context
 * @param {string} errorType - Type of error (e.g., 'api-error', 'component-error')
 * @param {Error|string} error - The error object or message
 * @param {object} context - Additional context about where/when the error occurred
 */
export const trackError = (errorType, error, context = {}) => {
  try {
    const errorMessage = error?.message || error?.toString() || 'Unknown error';
    const errorStack = error?.stack ? error.stack.split('\n')[0] : 'No stack trace';
    
    const errorProps = {
      errorType,
      errorMessage: errorMessage.substring(0, 100), // Limit length
      errorStack: errorStack.substring(0, 100),
      ...context
    };

    // Track to Plausible
    if (typeof window.plausible === 'function') {
      window.plausible('error-occurred', { props: errorProps });
    }

    // Always log to console for debugging
    console.error(`❌ ERROR [${errorType}]:`, {
      message: errorMessage,
      context,
      error
    });
  } catch (trackingError) {
    console.error('❌ Failed to track error:', trackingError);
  }
};
